import React, { useEffect, useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import RoundedPagination from './RoundedPagination';
import { getPrognosisContextColumns } from './prognosisTableModel';

export default function PrognosisResultsTable({ rows, groups, mode, categoryLabel, selectedRowKey, getRowKey, onGeneClick }) {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ id: 'gene_name', direction: 'asc' });
  useEffect(() => { setPage(1); }, [rows.length, mode]);
  const context = getPrognosisContextColumns(mode, categoryLabel);
  const columns = [...context, ...groups.flatMap(group => group.columns)];
  const sorted = [...rows];
  const activeColumn = columns.find(column => column.id === sort.id);
  if (activeColumn) sorted.sort((a, b) => {
    const left = activeColumn.value(a);
    const right = activeColumn.value(b);
    if (left == null || left === '') return right == null || right === '' ? 0 : 1;
    if (right == null || right === '') return -1;
    const result = String(left).localeCompare(String(right), undefined, { numeric: true });
    const numeric = Number(left) - Number(right);
    return (sort.direction === 'asc' ? 1 : -1) * (Number.isFinite(numeric) ? numeric : result);
  });
  const currentPage = Math.min(page, Math.max(1, Math.ceil(rows.length / 10)));
  const sortHeader = column => (
    <button type="button" onClick={() => { setSort({ id: column.id, direction: sort.id === column.id && sort.direction === 'asc' ? 'desc' : 'asc' }); setPage(1); }}>
      {column.label}<FiChevronDown aria-hidden="true" className={sort.id === column.id && sort.direction === 'asc' ? 'is-ascending' : ''} />
    </button>
  );
  const sortAria = column => sort.id === column.id ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none';

  return (
    <div className="prognosis-table-shell">
      <div className="prognosis-table-scroll" tabIndex={0} role="region" aria-label="Scrollable prognosis results">
        <table aria-label="Prognosis biomarker associations" style={{ minWidth: columns.reduce((sum, column) => sum + column.width, 0) }}>
          <colgroup>{columns.map(column => <col key={column.id} style={{ width: column.width }} />)}</colgroup>
          <thead>
            <tr>
              {context.map(column => <th key={column.id} scope="col" rowSpan={2} aria-sort={sortAria(column)}>{sortHeader(column)}</th>)}
              {groups.map(group => <th key={group.id} scope="colgroup" colSpan={group.columns.length} className={`metric-${group.id}`}><span className="prognosis-group-label"><i aria-hidden="true" />{group.label}</span></th>)}
            </tr>
            <tr>{groups.flatMap(group => group.columns.map(column => <th key={column.id} scope="col" className={`metric-${group.id}`} aria-sort={sortAria(column)}>{sortHeader(column)}</th>))}</tr>
          </thead>
          <tbody>
            {sorted.slice((currentPage - 1) * 10, currentPage * 10).map((row, index) => (
              <tr key={`${getRowKey(row)}-${index}`} className={selectedRowKey === getRowKey(row) ? 'is-selected' : ''} onClick={() => onGeneClick(row)}>
                {columns.map(column => {
                  const value = column.value(row);
                  const display = column.format ? column.format(value) : (value ?? '—');
                  return <td key={column.id} className={column.group ? `metric-${column.group}` : ''} title={String(display)}>
                    {column.id === 'gene_name' ? <button className="prognosis-gene-link" type="button" onClick={event => { event.stopPropagation(); onGeneClick(row); }} aria-label={`View details for ${row.gene_name}`}>{display}</button>
                      : column.id === 'category' ? <span className={`prognosis-category ${/protective/i.test(String(value)) ? 'is-protective' : /hazard|risk/i.test(String(value)) ? 'is-hazard' : ''}`}>{display}</span> : display}
                  </td>;
                })}
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={columns.length} className="prognosis-empty">No genes match the current search/filter.</td></tr>}
          </tbody>
        </table>
      </div>
      <RoundedPagination rowCount={rows.length} rowsPerPage={10} currentPage={currentPage} onChangePage={setPage} />
    </div>
  );
}
